import { ColorMode } from '../colorMode';

import { themeConfig } from './theme.config';
import {
  type ThemeId,
  type ThemePreference,
  type ThemePreferenceCustom,
} from './theme.types';

const CUSTOM_STYLE_ID = 'theme-custom-vars';
const allVariables = Object.values(themeConfig.regions).flat();

function buildDeclarations(overrides: Record<string, string>): string {
  return allVariables
    .filter((v) => overrides[v.key])
    .map((v) => `${v.cssVar}: ${overrides[v.key]};`)
    .join(' ');
}

/**
 * Applies the active theme. For named themes, sets `data-theme` on the root
 * element (CSS handles the variables). For custom themes, also injects a
 * `<style>` tag with the user's color overrides for both light and dark modes.
 */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', preference.id);

  if (preference.id === 'custom' && 'overrides' in preference) {
    const { light, dark } = preference.overrides;
    const lightDecl = buildDeclarations(light);
    const darkDecl = buildDeclarations(dark);

    let css = '';
    if (lightDecl) css += `[data-theme='custom'] { ${lightDecl} }\n`;
    if (darkDecl)
      css += `[data-color-mode='dark'][data-theme='custom'] { ${darkDecl} }\n`;

    let styleEl = document.getElementById(
      CUSTOM_STYLE_ID,
    ) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = CUSTOM_STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;
  } else {
    document.getElementById(CUSTOM_STYLE_ID)?.remove();
  }

  updateThemeColorMeta();
}

/** Updates the PWA theme-color meta tag to match the current sidebar background. */
export function updateThemeColorMeta(): void {
  requestAnimationFrame(() => {
    const bg = getComputedStyle(document.documentElement)
      .getPropertyValue('--sidebar-bg')
      .trim();

    if (bg) {
      let meta = document.querySelector<HTMLMetaElement>(
        'meta[name="theme-color"]',
      );
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', bg);
    }
  });
}

/**
 * Reads the computed CSS variable values for a given theme in both light and
 * dark color modes. Creates temporary hidden elements with the appropriate
 * `data-theme` and `data-color-mode` attributes to resolve values without
 * affecting the visible page.
 */
export function readThemeVariables(
  themeId: ThemeId | 'custom',
): ThemePreferenceCustom['overrides'] {
  function readMode(mode: ColorMode): Record<string, string> {
    const el = document.createElement('div');
    el.setAttribute('data-theme', themeId);
    el.setAttribute('data-color-mode', mode);
    el.style.position = 'absolute';
    el.style.visibility = 'hidden';
    el.style.pointerEvents = 'none';
    document.body.appendChild(el);

    const style = getComputedStyle(el);
    const colors: Record<string, string> = {};

    for (const v of allVariables) {
      const value = style.getPropertyValue(v.cssVar).trim();
      if (value) {
        colors[v.key] = value;
      }
    }

    document.body.removeChild(el);
    return colors;
  }

  return {
    light: readMode('light'),
    dark: readMode('dark'),
  };
}
